import Konva from 'konva';
import { ManualGrid } from '../grid_detection/manual_grid.js';

export const GridSettingsMethods = {
  _connectGridSettings() {
    this._gridSaved = (event) => this._refreshGridSettings(event.detail.url);
    this._gridManual = (event) => this._prepareGridCalibration(event);
    this._gridReady = (event) => {
      if (this.element.dataset?.konvaMapReady === 'true' && Array.isArray(this._shapes))
        event.detail.gridDependentLayers = this._shapes.some((shape) =>
          ['character', 'area-effect'].includes(shape.type)
        );
      const draft = this._gridDialogDraft;
      if (this._gridCalibration) this._endGridCalibration();
      if (draft && draft.imageId === event.detail.imageId) {
        event.detail.draft = draft.values;
      }
      this._gridDialogDraft = null;
    };
    this.element.addEventListener('grid-detection:manual', this._gridManual);
    this.element.addEventListener('grid-detection:ready', this._gridReady);
    this.element.addEventListener('grid-detection:updated', this._gridSaved);
    this._syncGridControls();
  },

  _disconnectGridSettings() {
    this._gridToggleRequest?.abort();
    this._gridRefreshRequest?.abort();
    this._endGridCalibration({ restoreTool: false });
    this.element.removeEventListener('grid-detection:manual', this._gridManual);
    this.element.removeEventListener('grid-detection:ready', this._gridReady);
    this.element.removeEventListener('grid-detection:updated', this._gridSaved);
  },

  async _refreshGridSettings(url) {
    this._gridRefreshRequest?.abort();
    const request = (this._gridRefreshRequest = new AbortController());
    try {
      const response = await fetch(url, {
        signal: request.signal,
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('The grid was saved, but the preview could not refresh. Reload the editor.');
      const state = await response.json();
      if (!request.signal.aborted) this._applyDetectedGridState(state);
    } catch (error) {
      if (!request.signal.aborted) this.workspaceInstructionTarget.textContent = error.message;
    } finally {
      if (this._gridRefreshRequest === request) this._gridRefreshRequest = null;
    }
  },

  _syncGridControls() {
    const [icon, label] = !this.gridEnabledValue
      ? ['fa-circle-question text-body-secondary', 'Grid disabled']
      : this.gridDetectionApproximateValue
        ? ['fa-triangle-exclamation text-warning', 'Grid needs adjustment']
        : ['fa-circle-check text-success', 'Grid enabled'];
    for (const status of this.gridStatusTargets || []) {
      status.className = `fa ${icon}`;
      status.setAttribute('aria-label', label);
    }
    for (const button of this.gridOverlayToggleTargets || []) {
      const label = this.gridHiddenValue ? 'Show grid overlay' : 'Hide grid overlay';
      button.title = label;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', String(!this.gridHiddenValue));
      button.disabled = !!this._gridToggleRequest || !this.gridEnabledValue;
      button.querySelector('i').className = this.gridHiddenValue ? 'fa fa-eye-slash' : 'fa fa-eye';
    }
  },

  async toggleGridOverlay() {
    if (this._gridToggleRequest || !this.gridEnabledValue || this.readonlyValue || this.playerValue) return;
    this._gridRefreshRequest?.abort();
    const previous = this.gridHiddenValue;
    const request = (this._gridToggleRequest = new AbortController());
    this.gridHiddenValue = !previous;
    this._syncGridControls();
    try {
      const response = await fetch(this.positionUpdateUrlValue, {
        method: 'PATCH',
        signal: request.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ game_map: { grid_hidden: this.gridHiddenValue } })
      });
      if (!response.ok) throw new Error('Grid visibility could not be saved. Please try again.');
      const state = await response.json();
      if (!request.signal.aborted) this._applyDetectedGridState(state);
    } catch (error) {
      if (!request.signal.aborted) {
        this.gridHiddenValue = previous;
        this.workspaceInstructionTarget.textContent = error.message;
      }
    } finally {
      if (this._gridToggleRequest === request) this._gridToggleRequest = null;
      this._syncGridControls();
    }
  },

  _prepareGridCalibration(event) {
    if (
      !this._stage ||
      !this._imgWidth ||
      !this._imgHeight ||
      this.readonlyValue ||
      this.playerValue ||
      event.detail.imageId !== this.reinitializeValue
    )
      return;
    event.preventDefault(); // Let the dialog know the editor accepted this request.
    this._gridDialogDraft = event.detail;
    this._gridCalibrationFrame = this._shapeModalFrame();
    this._gridCalibrationClosed = () => {
      if (!this._mapConnected || !this._stage || !this._gridDialogDraft) return;
      this._gridReturnTool = this._tool;
      this._gridReturnMode = this._workspaceMode;
      this._workspaceMode = 'editor';
      this._cancelCanvasGesture();
      this._applyTool('select');
      this._clearCharacterMove();
      this._deselectShape();
      this.closeLayers();
      this.closeFogBrush();
      this._gridCalibrationGroup = new Konva.Group({ listening: false });
      this._cursorLayer.add(this._gridCalibrationGroup);
      this.restartGridCalibration();
    };
    this._gridCalibrationFrame.addEventListener('modal:closed', this._gridCalibrationClosed, { once: true });
  },

  restartGridCalibration() {
    if (!this._gridDialogDraft || !this._gridCalibrationGroup) return;
    this._gridCalibration = new ManualGrid(this._imgWidth, this._imgHeight);
    this._gridCalibrationPointerDown = false;
    this.gridCalibrationPanelTarget.hidden = false;
    this.cursorTarget.style.cursor = 'crosshair';
    this._gridOverlay?.render();
    this._previewGridCalibration();
    this._syncWorkspaceControls();
  },

  _previewGridCalibration() {
    if (!this._gridCalibration) return;
    const { lines, square } = this._gridCalibration.preview(this._getWorldPointerPosition());
    const group = this._gridCalibrationGroup;
    group.destroyChildren();
    if (square) group.add(new Konva.Rect({ ...square, fill: 'rgba(52,211,153,0.2)', listening: false }));
    for (const points of lines) {
      group.add(
        new Konva.Line({ points, stroke: '#111827', strokeWidth: 4, strokeScaleEnabled: false, listening: false })
      );
      group.add(
        new Konva.Line({ points, stroke: '#6ee7b7', strokeWidth: 2, strokeScaleEnabled: false, listening: false })
      );
    }
    this.gridCalibrationMessageTarget.textContent = this._gridCalibration.instruction;
    this._cursorLayer.batchDraw();
  },

  _placeGridCalibration() {
    if (!this._gridCalibrationPointerDown) return;
    this._gridCalibrationPointerDown = false;
    const result = this._gridCalibration.place(this._getWorldPointerPosition());
    if (result) {
      Object.assign(this._gridDialogDraft.values, result, { grid_enabled: true, grid_detection_approximate: false });
      this._endGridCalibration({ reopen: true });
    } else this._previewGridCalibration();
  },

  cancelGridCalibration() {
    this._endGridCalibration({ reopen: true });
  },

  _endGridCalibration({ reopen = false, restoreTool = true } = {}) {
    this._gridCalibrationFrame?.removeEventListener('modal:closed', this._gridCalibrationClosed);
    const active = !!this._gridCalibration;
    this._gridCalibration = null;
    this._gridCalibrationPointerDown = false;
    this._gridCalibrationGroup?.destroy();
    this._gridCalibrationGroup = null;
    if (this.hasGridCalibrationPanelTarget) this.gridCalibrationPanelTarget.hidden = true;
    if (this.hasToolRailTarget) this.toolRailTarget.inert = false;
    if (active) {
      this._cursorLayer?.batchDraw();
      this._gridOverlay?.render();
      if (restoreTool) {
        this._workspaceMode = this._gridReturnMode;
        this._applyTool(this._gridReturnTool || 'select');
      }
    }
    if (reopen && this._mapConnected && this._gridDialogDraft) {
      this._shapeModalFrame().src = this._gridDialogDraft.url;
    } else this._gridDialogDraft = null;
  }
};
